import React, {useMemo, useState} from "react";
import {Box, Checkbox, Tooltip, Typography} from "@mui/material";
import {ChevronRight, ExpandMore} from "@mui/icons-material";
import {RichTreeView, TreeItem, type TreeItemProps} from "@mui/x-tree-view";
import type {FilterOption} from "../reducers/includeExcludeFilterReducer";
import {useTextTruncation} from "../../../utils/useTextTruncation";
import {
    computeNodeCheckState,
    getMatchingBranchIds,
    type TreeFilterItem,
} from "./treeViewFilterListSelectionUtils";

type FilterValueTreeProps = {
    /** Tree already pruned/sorted for the current search by the caller. */
    treeItems: TreeFilterItem[];
    /** Live "is this option selected?" predicate, keyed on the selection map. */
    isChecked: (option: FilterOption) => boolean;
    /** Toggle a node; caller owns the cascade + persistence. */
    onToggle: (option: FilterOption) => void;
    /**
     * When a search is active, matching branches auto-expand. Expansion is
     * otherwise ephemeral and starts collapsed — it is intentionally not
     * remembered across unmount/reopen (MER-305).
     */
    searchActive: boolean;
    /**
     * Identity of the current tab/context (e.g. "include" / "exclude"). Manual
     * expansion is tracked independently per scope so switching tabs shows that
     * tab's own expand/collapse state, while every scope still resets together
     * on unmount/reopen (MER-305: ephemeral, but independent per tab).
     */
    expansionScope?: string;
};

const DEFAULT_EXPANSION_SCOPE = "default";

function FilterValueTreeItemLabel({
    item,
    isChecked,
    onToggle,
}: {
    item: TreeFilterItem;
    isChecked: (option: FilterOption) => boolean;
    onToggle: (option: FilterOption) => void;
}) {
    const displayLabel =
        item.itemLabel?.trim() ||
        item.label?.trim() ||
        item.option.label?.trim() ||
        item.option.item?.trim() ||
        "";
    const {ref, isTruncated} = useTextTruncation([displayLabel]);
    const {checked, indeterminate} = computeNodeCheckState(item, isChecked);

    const handleToggle = (event: React.MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        onToggle(item.option);
    };

    return (
        <Tooltip title={isTruncated ? displayLabel : ""} followCursor>
            <Box
                onClick={handleToggle}
                sx={{
                    alignItems: "center",
                    display: "flex",
                    minWidth: 0,
                    pr: 2,
                    width: "100%",
                }}
            >
                <Checkbox
                    checked={checked}
                    indeterminate={indeterminate && !checked}
                    color="secondary"
                    disableRipple
                    edge="start"
                    onClick={handleToggle}
                    tabIndex={-1}
                />
                <Typography
                    ref={ref}
                    sx={{
                        display: "-webkit-box",
                        fontSize: "14px",
                        lineHeight: 1.2,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        WebkitBoxOrient: "vertical",
                        WebkitLineClamp: 2,
                    }}
                >
                    {displayLabel}
                </Typography>
            </Box>
        </Tooltip>
    );
}

/**
 * Presentational, controlled hierarchical value picker shared by the filter
 * dialog and the inline chip popover (MER-305), so browsing feels identical in
 * both. Selection is fully owned by the caller (via isChecked/onToggle);
 * expansion is local, ephemeral, and starts collapsed.
 */
export function FilterValueTree({
    treeItems,
    isChecked,
    onToggle,
    searchActive,
    expansionScope = DEFAULT_EXPANSION_SCOPE,
}: Readonly<FilterValueTreeProps>) {
    // Expansion is tracked per scope (tab) so Include and Exclude keep distinct
    // expand/collapse state while the picker stays open, yet every scope resets
    // together when the tree unmounts on reopen (MER-305).
    const [expandedByScope, setExpandedByScope] = useState<Record<string, string[]>>({});
    const manualExpandedItems = expandedByScope[expansionScope] ?? [];

    const matchingBranchIds = useMemo(
        () => getMatchingBranchIds(treeItems),
        [treeItems],
    );

    const treeItemsById = useMemo(() => {
        const entries = new Map<string, TreeFilterItem>();
        const visit = (items: TreeFilterItem[]) => {
            items.forEach((item) => {
                entries.set(item.id, item);
                if (item.children) {
                    visit(item.children);
                }
            });
        };
        visit(treeItems);
        return entries;
    }, [treeItems]);

    // While searching, the tree shows only matches + ancestors and every branch
    // is auto-expanded so results are visible; otherwise the scope's ephemeral
    // expansion (collapsed by default) drives it.
    const effectiveExpandedItems = searchActive ? matchingBranchIds : manualExpandedItems;

    const TreeItemSlot = useMemo(() => {
        const FilterTreeItem = React.forwardRef<HTMLLIElement, TreeItemProps>(
            function FilterTreeItem(props, ref) {
                const item = treeItemsById.get(String(props.itemId));
                if (!item) {
                    return <TreeItem {...props} ref={ref}/>;
                }
                return (
                    <TreeItem
                        {...props}
                        ref={ref}
                        label={
                            <FilterValueTreeItemLabel
                                item={item}
                                isChecked={isChecked}
                                onToggle={onToggle}
                            />
                        }
                    />
                );
            },
        );
        return FilterTreeItem;
    }, [treeItemsById, isChecked, onToggle]);

    return (
        // Remount the tree per scope so switching tabs presents that scope's
        // stored expansion immediately, instead of MUI animating the previous
        // tab's expanded nodes closed on a controlled-prop change. The scope map
        // lives in this (still-mounted) component, so per-tab state is retained
        // while open and only resets when the whole picker unmounts (MER-305).
        <RichTreeView
            key={expansionScope}
            items={treeItems}
            disableSelection
            expandedItems={effectiveExpandedItems}
            slots={{
                collapseIcon: ExpandMore,
                expandIcon: ChevronRight,
                item: TreeItemSlot,
            }}
            onExpandedItemsChange={(
                _: React.SyntheticEvent | null,
                itemIds: string[],
            ) => {
                // Ignore expansion changes while searching: the display is driven
                // by matchingBranchIds, so persisting them would overwrite the
                // scope's manual state and break restore-on-clear.
                if (searchActive) {
                    return;
                }
                setExpandedByScope((prev) => ({...prev, [expansionScope]: itemIds}));
            }}
            expansionTrigger="iconContainer"
            sx={{
                minHeight: 0,
                "& .MuiTreeItem-content": {
                    borderRadius: 1,
                    py: 0.25,
                },
                "& .MuiTreeItem-iconContainer": {
                    alignItems: "center",
                    cursor: "pointer",
                    display: "flex",
                    justifyContent: "center",
                    minWidth: 24,
                },
                "& .MuiTreeItem-label": {
                    width: "100%",
                },
            }}
        />
    );
}
